import type { MouseEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  classNames,
  legacyHookAttrs,
  legacyContractDataAttrs,
} from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { getMsgDeskRuntime } from '@/msgdeskRuntime'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { useEditorSurface } from '../../EditorSurfaceContext'

// Extended name key — legacy look (full.css: .mcss-namekeydiv-cssm / quick name
// key / code key editor) rebuilt in flex. Behaviour is routed through the
// runtime bridge; the look comes from the surface skin.

const quickRules = [
  { id: 0, label: 'numeric,' },
  { id: 1, label: 'code,' },
  { id: 2, label: 'secret code,' },
  { id: 3, label: 'by topic,' },
  { id: 4, label: 'by content,' },
  { id: 5, label: 'random word' },
]

function flushAfterLegacyCommand(reason: string) {
  getMsgDeskRuntime().setTimeout(() => dispatchEditorAction({ action: 'flush-model', reason }), 0)
}

function handleShowChangeCode() {
  dispatchEditorAction({ action: 'toggle-code-key', open: true })
}

function handleCheckCodeKey(event: MouseEvent<HTMLElement>) {
  dispatchEditorAction({ action: 'check-code-key', target: event.currentTarget })
  flushAfterLegacyCommand('react-head:after-check-code-key')
}

function handleCloseChangeCode() {
  dispatchEditorAction({ action: 'toggle-code-key', open: false })
}

export function ExtendedNameKey({ maxLength = 85, nameScope = '' }: { maxLength?: number; nameScope?: string }) {
  const { skin } = useEditorSurface()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const nameKeyAttrs = legacyContractDataAttrs('editor.nameKey.textarea')
  const contractRef = nameKeyAttrs.ref
  const setInputRef = useCallback((node: HTMLTextAreaElement | null) => {
    inputRef.current = node
    contractRef(node)
  }, [contractRef])
  const [status, setStatus] = useState('')
  useEffect(() => setStatus(''), [nameScope])
  const checkName = async (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    const input = inputRef.current
    if (!input || input.disabled) return
    input.disabled = true
    setStatus('Checking…')
    const result = await dispatchEditorAction({ action: 'check-extended-name-key', value: input.value })
    input.disabled = false
    input.value = result.value
    setStatus(result.status === 'ok' ? 'Available' : 'Name unavailable here. Check the name and parent access.')
    input.focus()
  }
  return (
    <div {...legacyHookAttrs('editor.extName.root', classNames('md-ext-name', skin.extName.root))}>
      <div className={classNames('md-ext-name__quick', skin.extName.quickRow)}>
        <span className={classNames('md-ext-name__label', skin.extName.label)}>Name:</span>
        {quickRules.map(rule => (
          <span
            key={rule.id}
            {...(rule.id === 0 ? legacyContractDataAttrs('editor.nameKey.defaultAuto') : {})}
            className={classNames('md-ext-name__quick-btn', skin.extName.quickBtn)}
            {...{ quick_id: String(rule.id) }}
          >
            {rule.label}
          </span>
        ))}
        <span style={{ display: 'none' }} aria-hidden="true">
        <span
          {...legacyContractDataAttrs('editor.nameKey.subKey')}
          className={classNames('md-ext-name__quick-btn md-ext-name__subkey', skin.extName.subKeyBtn)}
        >
          &gt;*&lt;
        </span>
        </span>
      </div>
      <div className={classNames('md-ext-name__box', skin.extName.box)}>
        <textarea
          {...nameKeyAttrs}
          ref={setInputRef}
          className={classNames('md-ext-name__textarea', skin.extName.textarea)}
          name="name_key"
          maxLength={maxLength}
          onInput={event => {
            setStatus('')
            dispatchEditorAction({ action: 'set-extended-name-key', value: event.currentTarget.value })
          }}
        />
        <div {...legacyContractDataAttrs('editor.nameKey.mirror')} className={classNames('md-ext-name__mirror', skin.extName.mirror)} />
        <div className={classNames('md-ext-name__check-row', skin.extName.checkRow)}>
          <span {...legacyContractDataAttrs('editor.nameKey.subKey')} className={classNames('md-ext-name__subkey-text', skin.extName.subKeyText)} />
          <button type="button" {...legacyContractDataAttrs('editor.nameKey.check')} className={classNames('md-ext-name__check', skin.extName.check)} onClick={checkName}>Ok!</button>
          <span {...legacyContractDataAttrs('editor.nameKey.emailInfo')} className={classNames('md-ext-name__email-info', skin.extName.emailInfo)}>Enter and verify the name</span>
          <span {...legacyContractDataAttrs('editor.nameKey.showChangeCode')} className={classNames('md-ext-name__email-edit', skin.extName.emailEdit)} onClick={handleShowChangeCode}>(Edit)</span>
        </div>
      </div>
      {status && <span role="status">{status}</span>}
      <div className="md-ext-name__errors" />
      <div {...legacyContractDataAttrs('editor.nameKey.changeCode')} className={classNames('md-ext-name__code', skin.extName.code)}>
        Email:
        <input
          type="text"
          className={classNames('md-ext-name__code-input', skin.extName.codeInput)}
          name="code_key"
          onInput={event => dispatchEditorAction({ action: 'set-code-key', value: event.currentTarget.value })}
        />
        @msgdsk.com
        <span {...legacyContractDataAttrs('editor.nameKey.checkCode')} className={classNames('md-ext-name__code-btn', skin.extName.codeBtn)} onClick={handleCheckCodeKey}>Check!</span>
        <span {...legacyContractDataAttrs('editor.nameKey.closeChangeCode')} className={classNames('md-ext-name__code-btn', skin.extName.codeBtn)} onClick={handleCloseChangeCode}>Close</span>
      </div>
      <span {...legacyContractDataAttrs('editor.nameKey.codeKeyInfo')} className={classNames('md-ext-name__code-info', skin.extName.codeInfo)} />
    </div>
  )
}
